// File: hrDocumentService.js
import axios from "axios";
import { Base_URL } from "../url";

// Create axios instance with interceptors for debugging
const hrApi = axios.create({
  baseURL: Base_URL,
  timeout: 30000,
});

// Add request interceptor for debugging
hrApi.interceptors.request.use(
  (config) => {
    console.group(`🚀 HR ${config.method?.toUpperCase()} ${config.url}`);
    console.log('📦 Headers:', config.headers);
    console.log('🔧 Config:', config);
    
    if (config.data instanceof FormData) {
      console.log('📁 FormData contents:');
      for (let pair of config.data.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}:`, {
            name: pair[1].name,
            size: pair[1].size,
            type: pair[1].type
          });
        } else {
          console.log(`  ${pair[0]}:`, pair[1]);
        }
      }
    } else {
      console.log('📝 Data:', config.data);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
hrApi.interceptors.response.use(
  (response) => {
    console.log('✅ Response:', {
      status: response.status,
      data: response.data
    });
    console.groupEnd();
    return response;
  },
  (error) => {
    console.error('❌ Response error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      config: error.config
    });
    console.groupEnd();
    return Promise.reject(error);
  }
);

export const hrDocumentService = {
  // Upload ID/Bank document for any user (HR/Admin use)
  async uploadDocumentForUser(userId, organisationId, documentType, file) {
    console.group('📤 HR Document Upload Service');
    
    if (!userId || !organisationId) {
      console.error('❌ Missing user details:', { userId, organisationId });
      throw new Error('User ID and Organisation ID are required for HR operations.');
    }
    
    // Build FormData
    const formData = new FormData();
    formData.append('File', file);
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    formData.append('DocumentType', documentType);
    
    try {
      const response = await hrApi.post('/document/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ HR Upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ HR Upload failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Delete document (same as user service but with HR context)
  async deleteDocument(documentId) {
    console.group('🗑️ HR Delete Document');
    
    try {
      const response = await hrApi.delete(`/document/${documentId}`);
      console.log('✅ HR Delete successful! Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ HR Delete failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get documents for any user (HR/Admin use)
  async getUserDocuments(userId) {
    console.group('📋 HR Get User Documents');
    
    try {
      const response = await hrApi.get(`/document/user/${userId}`);
      console.log('✅ HR Get documents response:', response.data);
      
      if (response.data.success && Array.isArray(response.data.documents)) {
        return { success: true, documents: response.data.documents };
      } else if (Array.isArray(response.data)) {
        return { success: true, documents: response.data };
      } else if (Array.isArray(response.data?.data)) {
        return { success: true, documents: response.data.data };
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        return { success: false, documents: [] };
      }
    } catch (error) {
      console.error('❌ HR Get documents failed:', error);
      return { success: false, documents: [] };
    } finally {
      console.groupEnd();
    }
  },

  // View document (opens in new tab)
  async viewDocument(documentId) {
    console.group('👁️ HR View Document');
    
    try {
      const response = await hrApi.get(`/document/view/${documentId}`);
      
      if (response.data.success && response.data.url) {
        console.log('✅ Opening document URL:', response.data.url);
        window.open(response.data.url, '_blank');
        return { success: true, url: response.data.url };
      } else {
        console.error('❌ No URL found in response');
        throw new Error('Document URL not found');
      }
    } catch (error) {
      console.error('❌ HR View document failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get document types
  async getDocumentTypes() {
    console.group('📋 HR Get Document Types');
    
    try {
      const response = await hrApi.get('/document/types');
      console.log('✅ Get document types successful! Found:', response.data.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Get document types failed:', error);
      return [];
    } finally {
      console.groupEnd();
    }
  }
};

export const hrQualificationService = {
  // Upload multiple qualifications for any user
 async uploadQualificationsBulk(userId, organisationId, qualifications) {
    console.group('📤 HR Bulk Qualifications Upload');
    
    if (!userId || !organisationId) {
      console.error('❌ Missing user details:', { userId, organisationId });
      throw new Error('User information is required for HR operations.');
    }
    
    console.log('📋 Received qualifications:', qualifications);
    
    // Validate qualifications - more detailed validation
    const validQualifications = qualifications.filter((qual, index) => {
      console.log(`🔍 Validating qualification ${index}:`, qual);
      
      const isValid = qual.qualificationType && 
                     qual.institute && 
                     qual.year && 
                     qual.attachment &&
                     qual.attachment instanceof File;
      
      if (!isValid) {
        console.warn(`⚠️ Qualification ${index} invalid:`, {
          hasQualificationType: !!qual.qualificationType,
          hasInstitute: !!qual.institute,
          hasYear: !!qual.year,
          hasAttachment: !!qual.attachment,
          isFileInstance: qual.attachment instanceof File,
          attachmentType: qual.attachment?.constructor?.name
        });
      }
      
      return isValid;
    });
    
    console.log(`📊 Valid qualifications: ${validQualifications.length}/${qualifications.length}`);
    
    if (validQualifications.length === 0) {
      throw new Error('Please fill all required fields and upload files for at least one qualification.');
    }
    
    // Build FormData
    const formData = new FormData();
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    
    // Add each qualification
    validQualifications.forEach((qual, index) => {
      console.log(`📦 Adding qualification ${index} to FormData:`, {
        qualificationType: qual.qualificationType,
        institute: qual.institute,
        year: qual.year,
        grade: qual.grade
      });
      
      formData.append(`Qualifications[${index}].QualificationType`, qual.qualificationType);
      formData.append(`Qualifications[${index}].Institute`, qual.institute);
      formData.append(`Qualifications[${index}].Year`, qual.year.toString());
      formData.append(`Qualifications[${index}].Grade`, qual.grade || '');
      
      if (qual.attachment && qual.attachment instanceof File) {
        console.log(`📎 Adding file ${index}:`, {
          name: qual.attachment.name,
          size: qual.attachment.size,
          type: qual.attachment.type
        });
        formData.append(`Qualifications[${index}].File`, qual.attachment);
      } else {
        console.error(`❌ Qualification ${index} has invalid attachment:`, qual.attachment);
      }
    });
    
    // Log FormData contents for debugging
    console.log('📁 FormData entries:');
    for (let pair of formData.entries()) {
      console.log(`  ${pair[0]}:`, pair[1]);
    }
    
    try {
      const response = await hrApi.post('/qualification/upload-bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ HR Bulk upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ HR Bulk upload failed:', error);
      console.error('❌ Error response:', error.response?.data);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Upload single qualification for any user
  async uploadQualification(userId, organisationId, qualificationData) {
    console.group('📤 HR Single Qualification Upload');
    
    if (!userId || !organisationId) {
      console.error('❌ Missing user details:', { userId, organisationId });
      throw new Error('User information is required for HR operations.');
    }
    
    // Validate required fields
    if (!qualificationData.qualificationType || !qualificationData.institute || 
        !qualificationData.year || !qualificationData.file) {
      throw new Error('Please fill all required fields and upload a file.');
    }
    
    // Build FormData for single upload
    const formData = new FormData();
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    formData.append('QualificationType', qualificationData.qualificationType);
    formData.append('Institute', qualificationData.institute);
    formData.append('Year', qualificationData.year.toString());
    formData.append('Grade', qualificationData.grade || '');
    formData.append('File', qualificationData.file);
    
    try {
      const response = await hrApi.post('/qualification/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ HR Single qualification upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ HR Single qualification upload failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get user qualifications
  async getUserQualifications(userId) {
    console.group('📋 HR Get User Qualifications');
    
    try {
      const response = await hrApi.get(`/qualification/user/${userId}`);
      console.log('✅ HR Get qualifications successful! Found:', response.data.qualifications?.length || 0);
      
      if (response.data.success && Array.isArray(response.data.qualifications)) {
        return { success: true, qualifications: response.data.qualifications };
      } else if (Array.isArray(response.data)) {
        return { success: true, qualifications: response.data };
      } else {
        return { success: false, qualifications: [] };
      }
    } catch (error) {
      console.error('❌ HR Get qualifications failed:', error);
      return { success: false, qualifications: [] };
    } finally {
      console.groupEnd();
    }
  },

  // Delete qualification
  async deleteQualification(qualificationId) {
    console.group('🗑️ HR Delete Qualification');
    
    try {
      const response = await hrApi.delete(`/qualification/${qualificationId}`);
      console.log('✅ HR Delete successful! Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ HR Delete failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get qualification by ID
  async getQualificationById(qualificationId) {
    console.group('🔍 HR Get Qualification By ID');
    
    try {
      const response = await hrApi.get(`/qualification/${qualificationId}`);
      console.log('✅ HR Get qualification successful!', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ HR Get qualification failed:', error);
      return { success: false, message: 'Qualification not found' };
    } finally {
      console.groupEnd();
    }
  }
};

// Optional: Combined service that can detect context
export const universalDocumentService = {
  // Smart upload that detects if it's HR context or user context
  async uploadDocument(context, documentType, file, userId = null, organisationId = null) {
    if (context === 'hr' && userId && organisationId) {
      return hrDocumentService.uploadDocumentForUser(userId, organisationId, documentType, file);
    } else {
      // Fall back to user service
      const userDetailsStr = localStorage.getItem('userDetails');
      const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : null;
      const targetUserId = userId || userDetails?.userId;
      
      if (!targetUserId) {
        throw new Error('User ID is required');
      }
      
      // You would need to import the original documentService here
      // For now, we'll assume it's available
      const { documentService } = await import('./documentService');
      return documentService.uploadDocument(targetUserId, documentType, file);
    }
  },
  
  // Similar pattern for other methods
  async getUserDocuments(context, userId = null) {
    if (context === 'hr' && userId) {
      return hrDocumentService.getUserDocuments(userId);
    } else {
      const userDetailsStr = localStorage.getItem('userDetails');
      const userDetails = userDetailsStr ? JSON.parse(userDetailsStr) : null;
      const targetUserId = userId || userDetails?.userId;
      
      if (!targetUserId) {
        throw new Error('User ID is required');
      }
      
      const { documentService } = await import('./documentService');
      return documentService.getUserDocuments(targetUserId);
    }
  }
};